import CloseIcon from "@mui/icons-material/Close";
import Avatar from "@mui/material/Avatar";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import CircularProgress from "@mui/material/CircularProgress";
import Grid from "@mui/material/Grid";
import IconButton from "@mui/material/IconButton";
import Paper from "@mui/material/Paper";
import Typography from "@mui/material/Typography";
import React, { useEffect, useState } from "react";

import BasicInfo from "./components/BasicInfo";
import Contact from "./components/Contact";

import { zodResolver } from "@hookform/resolvers/zod";
import { useForm, useWatch } from "react-hook-form";
import { ReactComponent as BuildingIcon } from "../../assets/Icons/building.svg";
import { ReactComponent as UploadIcon } from "../../assets/Icons/upload.svg";
import AboutMe from "../../components/BasicProfile/AboutMe";
import ImageCropperModal from "../../components/ImageCropperModal";
import { LOADING, SUCCESS } from "../../constants/common";
import { useAlert } from "../../contexts/Alert";
import { useGeneralProfile } from "../../contexts/Organization";
import { useImageUpload } from "../../hooks/useImageUpload";
import { profileImageValidationSchema } from "../../validations/profileValidations";
import { trackClickEvent } from "../../mixpanel";

const ProfileOrganization = () => {
  const {
    facilityInfo,
    fetchFacilityInfo,
    editFacilityPhoto,
    updatePhotoStatus,
    updateGeneralProfileState,
    removeFacilityProfilePhoto,
  } = useGeneralProfile();
  const { sendAlert } = useAlert();

  const {
    control,
    formState: { errors },
    setValue,
  } = useForm({
    resolver: zodResolver(profileImageValidationSchema),
    mode: "onChange",
    defaultValues: {
      main_image: facilityInfo.main_image || null,
    },
  });

  const formValues = useWatch({ control });

  const {
    profileImageURL,
    setProfileImageURL,
    cropperOpen,
    setCropperOpen,
    tempImageSrc,
    error: uploadError,
    handleFileInput,
    handleCroppedImage,
    handleSkipCrop,
  } = useImageUpload({
    onUpload: (file) => {
      setValue("main_image", file);
      editFacilityPhoto(file);
    },
    onError: (message) => sendAlert(message, "error"),
  });

  useEffect(() => {
    if (formValues.main_image && typeof formValues.main_image === "string") {
      setProfileImageURL(formValues.main_image);
    }
  }, [formValues]);

  useEffect(() => {
    if (facilityInfo.main_image) {
      setValue("main_image", facilityInfo.main_image);
      setProfileImageURL(facilityInfo.main_image);
    }
  }, [facilityInfo.main_image]);

  const handleDeleteImage = async () => {
    try {
      await removeFacilityProfilePhoto();
      setProfileImageURL(null);
      trackClickEvent("Updated Profile Information", {
        component: "Basic Organization info",
        page: "Edit Profile",
        action: "Delete Image",
      });
    } catch {
      sendAlert("Something went wrong while removing profile picture", "error");
    }
  };

  useEffect(() => {
    fetchFacilityInfo();
  }, []);

  useEffect(() => {
    if (updatePhotoStatus === SUCCESS) {
      updateGeneralProfileState("updatePhotoStatus", "");
      fetchFacilityInfo();
    }
  }, [updatePhotoStatus]);

  return (
    <Paper
      sx={{
        p: 3,
        display: "flex",
        flexDirection: "column",
        gap: 3,
      }}
    >
      <Box sx={{ display: "flex", alignItems: "center" }}>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            color: "border.dark",
          }}
        >
          <BuildingIcon width={24} height={24} />
        </Box>
        <Typography variant="h4" ml={1}>
          Organization Settings
        </Typography>
      </Box>
      <Grid container spacing={3}>
        <Grid item xs={8}>
          <BasicInfo />
        </Grid>
        <Grid item xs={4}>
          <Box
            sx={{
              borderWidth: "1px",
              borderStyle: "solid",
              borderColor: "border.main",
              p: 3,
              borderRadius: 2,
            }}
          >
            <Typography variant="h5" mb={3}>
              Upload logo
            </Typography>
            {updatePhotoStatus === LOADING ? (
              <Box
                sx={{
                  border: "1px dashed",
                  borderColor: "border.dark",
                  borderRadius: 1.5,
                  height: 145,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  cursor: "pointer",
                  position: "relative",
                  "&:hover": {
                    backgroundColor: "primary.light",
                  },
                }}
              >
                <CircularProgress />
              </Box>
            ) : (
              <Box>
                <Box
                  sx={{
                    border: "1px dashed",
                    borderColor: "border.dark",
                    borderRadius: 1.5,
                    overflow: "hidden",
                    height: 158,
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    justifyContent: "center",
                    position: "relative",
                    cursor: "pointer",
                    "&:hover": {
                      backgroundColor: profileImageURL
                        ? "transparent"
                        : "primary.light",
                    },
                  }}
                >
                  {profileImageURL ? (
                    <>
                      {/* Clickable Avatar */}
                      <label
                        style={{
                          width: "100%",
                          height: "100%",
                          cursor: "pointer",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          position: "relative",
                        }}
                      >
                        <Avatar
                          src={profileImageURL}
                          sx={{
                            width: "100%",
                            height: "auto",
                            borderRadius: 0,
                          }}
                        />
                        <Typography
                          variant="body2"
                          sx={{
                            textAlign: "center",
                            position: "absolute",
                            bottom: 0,
                            backgroundColor: "rgba(0,0,0,0.4)",
                            width: "100%",
                            color: "background.paper",
                          }}
                        >
                          Edit
                        </Typography>
                        <input
                          type="file"
                          accept="image/*"
                          onChange={handleFileInput}
                          style={{ display: "none" }}
                          name="main_image"
                        />
                      </label>
                      {/* Delete Button */}
                      <IconButton
                        size="small"
                        aria-label="close"
                        color="inherit"
                        onClick={handleDeleteImage}
                        sx={{
                          position: "absolute",
                          top: 5,
                          right: 5,
                          width: 22,
                          height: 22,
                          backgroundColor: "background.paper",
                          borderRadius: "50%",
                          "&:hover": {
                            backgroundColor: "error.light",
                            color: "error.main",
                          },
                          zIndex: 10,
                        }}
                      >
                        <CloseIcon fontSize="small" />
                      </IconButton>
                    </>
                  ) : (
                    <label
                      style={{
                        cursor: "pointer",
                        display: "flex",
                        justifyContent: "center",
                        width: "100%",
                        height: "100%",
                      }}
                    >
                      <Button
                        startIcon={<UploadIcon />}
                        sx={{
                          p: 0,
                          "&:hover": {
                            backgroundColor: "transparent",
                          },
                        }}
                        component="span"
                      >
                        Upload
                      </Button>
                      <input
                        type="file"
                        accept="image/*"
                        onChange={handleFileInput}
                        style={{ display: "none", overflow: "hidden" }}
                        name="main_image"
                      />
                    </label>
                  )}
                  {(!!errors.main_image || uploadError) && (
                    <Typography
                      variant="caption"
                      color="error"
                      sx={{ textAlign: "center", p: 1 }}
                    >
                      {errors.main_image?.message || uploadError}
                    </Typography>
                  )}
                </Box>
              </Box>
            )}
          </Box>
        </Grid>
      </Grid>
      <Contact />
      <AboutMe />
      <ImageCropperModal
        open={cropperOpen}
        onClose={() => setCropperOpen(false)}
        imageSrc={tempImageSrc}
        onCrop={handleCroppedImage}
        onSkip={handleSkipCrop}
      />
    </Paper>
  );
};

export default ProfileOrganization;
